/**
 * RemoveSourceModal — Tank Redesign port.
 *
 * Confirms removing a "bad match source" embedding for a face. After the
 * root-tracking backend landed, the modal can show both the immediate
 * parent (the embedding this face was matched against at assignment
 * time) and the root anchor (the labelled embedding the chain
 * originates from) plus a count of dependents tracing back to the root.
 *
 * Layout uses redesign primitives (Eyebrow + Mono + Btn) and is
 * scannable: "Person" → "What you're removing" → "What will happen" →
 * footer with explicit action buttons. Two-button footer when a root
 * cascade is available (Remove parent only / Remove root + N), single
 * destructive Remove Source otherwise.
 */

import Modal from '../../../../shared/components/ui/Modal';
import Spinner from '../../../../shared/components/ui/Spinner';
import { Btn, Eyebrow, Mono, StatusPill } from '../../../../shared/components/redesign';
import styles from './RemoveSourceModal.module.css';

function formatTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SourceRow({ label, detail, secondary }) {
  if (!detail) return null;
  const kind = detail.source_type === 'training' ? 'training' : 'pipeline';
  return (
    <div className={styles.row}>
      <div className={styles.rowHead}>
        <Mono className={styles.rowLabel}>{label}</Mono>
        <span className={styles.rowKind}>{kind} embedding</span>
        {detail.is_deleted && <StatusPill status="error">deleted</StatusPill>}
        {detail.is_inferred && <StatusPill status="attention">inferred</StatusPill>}
      </div>
      <div className={styles.rowMeta}>
        <Mono className={styles.metaKey}>captured</Mono>
        <span className={styles.metaValue}>{formatTs(detail.source_created_at)}</span>
        {detail.source_distance != null && (
          <>
            <Mono className={styles.metaKey}>distance</Mono>
            <span className={styles.metaValue}>{detail.source_distance.toFixed(4)}</span>
          </>
        )}
        {secondary}
      </div>
      {detail.source_image_id && (
        <Mono className={styles.rowId} title={detail.source_image_id}>
          {detail.source_image_id}
        </Mono>
      )}
    </div>
  );
}

export default function RemoveSourceModal({
  isOpen,
  loading,
  sourceInfo,
  onCancel,
  onConfirm,
}) {
  const source = sourceInfo?.source ?? null;
  const root = source?.root ?? null;
  const dependentCount = root?.dependent_count ?? 0;
  const offerCascade = !!root && dependentCount > 0 && !root.is_deleted;
  const noActionable =
    !source || (source.is_deleted && !offerCascade);

  return (
    <Modal isOpen={isOpen} onClose={onCancel} size="md">
      <Modal.Header onClose={onCancel}>Remove bad match source</Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className={styles.loading}>
            <Spinner size="sm" /> <span>Loading source info…</span>
          </div>
        ) : sourceInfo?.error ? (
          <p className={styles.error}>Failed to load source information.</p>
        ) : !source ? (
          <p className={styles.empty}>
            No source embedding recorded for this face. Nothing to remove —
            you can still unassign the person from this face via the
            standard menu.
          </p>
        ) : (
          <div className={styles.body}>
            <section className={styles.section}>
              <Eyebrow>Person</Eyebrow>
              <p className={styles.personName}>
                {sourceInfo.person_name || 'Unknown'}
              </p>
            </section>

            <section className={styles.section}>
              <Eyebrow>What you're removing</Eyebrow>
              <SourceRow label="Parent" detail={source} />
              {root && (
                <SourceRow
                  label="Root anchor"
                  detail={root}
                  secondary={
                    <>
                      <Mono className={styles.metaKey}>dependents</Mono>
                      <span className={styles.metaValue}>
                        {dependentCount} face{dependentCount === 1 ? '' : 's'}
                      </span>
                    </>
                  }
                />
              )}
              {!root && (
                <p className={styles.note}>
                  This face was matched directly against the labelled anchor —
                  there's no separate root, parent <em>is</em> the root.
                </p>
              )}
            </section>

            <section className={styles.section}>
              <Eyebrow>What will happen</Eyebrow>
              {source.is_deleted && !offerCascade ? (
                <p className={styles.note}>
                  The recorded source was already removed in a prior action.
                  Nothing left to delete here.
                </p>
              ) : offerCascade ? (
                <ul className={styles.bulletList}>
                  <li>
                    <strong>Remove parent only</strong> — soft-deletes just
                    the parent embedding shown above. New auto-matches won't
                    reach for it. Other dependents tracing back to the same
                    root anchor keep their person assignment.
                  </li>
                  <li>
                    <strong>Remove root + {dependentCount}</strong> —
                    soft-deletes the root anchor <em>and</em> returns all{' '}
                    {dependentCount} dependent face
                    {dependentCount === 1 ? '' : 's'} to pending. Use this
                    when the original labelled embedding is bad and you want
                    to break the entire chain in one click.
                  </li>
                </ul>
              ) : (
                <p className={styles.note}>
                  Soft-deletes the source embedding above so it stops
                  anchoring future matches, and unassigns the person from
                  this face. Other faces of {sourceInfo.person_name || 'this person'}{' '}
                  stay tagged.
                </p>
              )}
            </section>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.footer}>
          <Btn kind="ghost" onClick={onCancel}>
            Cancel
          </Btn>
          <div className={styles.footerRight}>
            {!noActionable && offerCascade && (
              <>
                <Btn
                  kind="soft"
                  onClick={() => onConfirm({ cascade: false })}
                  disabled={loading}
                >
                  Remove parent only
                </Btn>
                <Btn
                  kind="danger"
                  onClick={() => onConfirm({ cascade: true })}
                  disabled={loading}
                >
                  Remove root + {dependentCount}
                </Btn>
              </>
            )}
            {!noActionable && !offerCascade && (
              <Btn
                kind="danger"
                onClick={() => onConfirm({ cascade: false })}
                disabled={loading}
              >
                Remove source
              </Btn>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
